// @flow

import actionCreators from "@modules/reporting/actionCreator";
import { all, call, put, takeEvery } from "redux-saga/effects";
import {
  GetAbsenceRecordSearchFiltersSchema,
  CreateAbsenceSavedReport,
  GetAbsenceDefaultReports,
  GetAbsenceMySavedReports,
  GetAbsenceDetailsReport,
  DeleteSavedAbsenceDetailsReport,
  UpdateSavedAbsenceDetailsReport,
  GetAbsenceDetailsCharts,
  ExportAbsenceDetailsReport,
  ExportAbsenceDetailsReportSuccess,
} from "@modules/reporting/constants/actions";
import http from "@http";
import type { Action } from "redux-action";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";
import config from "@config";

type GetAbsenceReportsRequest = $Exact<{
  clientId: number,
}>;

export function* onGetAbsenceRecordSearchFiltersSchema({
  payload,
}: Action<GetAbsenceReportsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const schema = yield parseSchema("AbsenceDetailsReportFiltersDTO", {
      clientId: payload.clientId,
    });

    yield put(actionCreators.GetAbsenceRecordSearchFiltersSchemaSuccess(schema));
  } catch (e) {
    yield put(actionCreators.GetAbsenceRecordSearchFiltersSchemaError(e.response));
  }
}

export function* onGetAbsenceDefaultReports({
  payload,
}: Action<GetAbsenceReportsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/default-reports`,
    );

    yield put(actionCreators.GetAbsenceDefaultReportsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceDefaultReportsError(e.response));
  }
}

export function* onGetAbsenceMySavedReports({
  payload,
}: Action<GetAbsenceReportsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }
  try {
    const response = yield call(
      http.get,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/my-saved-reports`,
    );

    yield put(actionCreators.GetAbsenceMySavedReportsSuccess(response.data));
    yield put(actionCreators.ReSetAbsenceDetailsReportToSave());
  } catch (e) {
    yield put(actionCreators.GetAbsenceMySavedReportsError(e.response));
  }
}

type CreateAbsenceSavedReportRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
  name: string,
  callBack: () => void,
}>;

type CreateAbsenceSavedReportChartsRequest = $Exact<{
  clientId: number,
  filters: Object,
  columns: string[],
}>;

type DeleteAbsenceSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  successMessage: string,
}>;

type UpdateAbsenceSavedReportRequest = $Exact<{
  clientId: number,
  reportId: number,
  data: Object,
  shouldFetchReportData?: boolean,
}>;

export function* onCreateAbsenceSavedReport({
  payload,
}: Action<CreateAbsenceSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.name) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/my-saved-reports`,
      {
        name: payload.name,
        filters: payload.filters,
        columns: payload.columns || [],
      },
    );

    yield all([
      put(actionCreators.CreateAbsenceSavedReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceMySavedReports({ clientId: payload.clientId })),
      put(
        actionCreators.GetAbsenceDetailsReport({
          clientId: payload.clientId,
          report: response.data,
        }),
      ),
      put(
        actionCreators.GetAbsenceDetailsCharts({
          clientId: payload.clientId,
          filters: response.data.filters,
        }),
      ),
      put(sendSuccessMessage("REPORT_CREATED")),
    ]);
  } catch (e) {
    yield put(actionCreators.CreateAbsenceSavedReportError(e.response));
  }
}

type GetAbsenceDetailsReportRequest = $Exact<{
  clientId: number,
  report: Object,
  pageable: Object,
  callBack?: () => void,
}>;

export function* onGetAbsenceDetailsReport({
  payload,
}: Action<GetAbsenceDetailsReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.report) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details`,
      payload.report.filters || {},
      {
        params: {
          ...payload.pageable,
          limit: config.results50PerPage,
        },
      },
    );

    yield put(actionCreators.GetAbsenceDetailsReportSuccess(response.data));
    payload.callBack && payload.callBack();
  } catch (e) {
    yield put(actionCreators.GetAbsenceDetailsReportError(e.response));
  }
}

export function* onGetAbsenceDetailsCharts({
  payload,
}: Action<CreateAbsenceSavedReportChartsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/charts`,
      payload.filters,
    );

    yield put(actionCreators.GetAbsenceDetailsChartsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAbsenceDetailsChartsError(e.response));
  }
}

export function* onUpdateSavedAbsenceDetailsReport({
  payload,
}: Action<UpdateAbsenceSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.data) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.put,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/my-saved-reports/${payload.reportId}`,
      payload.data,
    );
    const additionalActions = payload.shouldFetchReportData
      ? [
          put(
            actionCreators.GetAbsenceDetailsReport({
              clientId: payload.clientId,
              report: response.data,
            }),
          ),
          put(
            actionCreators.GetAbsenceDetailsCharts({
              clientId: payload.clientId,
              filters: response.data.filters,
            }),
          ),
        ]
      : [];

    yield all([
      put(actionCreators.UpdateSavedAbsenceDetailsReportSuccess(response.data)),
      put(actionCreators.ToggleSaveReportModal({ visible: false })),
      put(actionCreators.GetAbsenceMySavedReports({ clientId: payload.clientId })),
      put(sendSuccessMessage("REPORT_UPDATED")),
      ...additionalActions,
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateSavedAbsenceDetailsReportError(e.response));
  }
}

export function* onDeleteSavedAbsenceDetailsReport({
  payload,
}: Action<DeleteAbsenceSavedReportRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.reportId || !payload.successMessage) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    yield call(
      http.delete,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/my-saved-reports/${payload.reportId}`,
    );

    yield all([
      put(actionCreators.DeleteSavedAbsenceDetailsReportSuccess()),
      put(actionCreators.GetAbsenceMySavedReports({ clientId: payload.clientId })),
      put(sendSuccessMessage(payload.successMessage)),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteSavedAbsenceDetailsReportError(e.response));
  }
}

export function* onExportAbsenceDetailsReport({
  payload,
}: Action<CreateAbsenceSavedReportChartsRequest>): Generator<*, void, *> {
  if (!payload || !payload.clientId || !payload.filters || !payload.columns) {
    throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
  }

  try {
    const response = yield call(
      http.post,
      `/api/v1/reports/client/${payload.clientId}/reports/absence-details/export`,
      { filters: payload.filters, columns: payload.columns },
      {
        headers: {
          Accept: "text/csv",
        },
      },
    );

    yield put(actionCreators.ExportAbsenceDetailsReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportAbsenceDetailsReportError(e.response));
  }
}

export function* onExportAbsenceDetailsReportSuccess({
  payload,
}: Action<any>): Generator<*, void, any> {
  yield downloadCsvFile(payload, "absence-details-report.csv");
}

export function* AbsenceDetailsSaga(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAbsenceRecordSearchFiltersSchema, onGetAbsenceRecordSearchFiltersSchema),
    takeEvery(CreateAbsenceSavedReport, onCreateAbsenceSavedReport),
    takeEvery(GetAbsenceDefaultReports, onGetAbsenceDefaultReports),
    takeEvery(GetAbsenceMySavedReports, onGetAbsenceMySavedReports),
    takeEvery(GetAbsenceDetailsReport, onGetAbsenceDetailsReport),
    takeEvery(DeleteSavedAbsenceDetailsReport, onDeleteSavedAbsenceDetailsReport),
    takeEvery(UpdateSavedAbsenceDetailsReport, onUpdateSavedAbsenceDetailsReport),
    takeEvery(GetAbsenceDetailsCharts, onGetAbsenceDetailsCharts),
    takeEvery(ExportAbsenceDetailsReport, onExportAbsenceDetailsReport),
    takeEvery(ExportAbsenceDetailsReportSuccess, onExportAbsenceDetailsReportSuccess),
  ]);
}

export default AbsenceDetailsSaga;
